import type { EnvironmentPatch, MapSettingsRequest } from './assistant-actions'

type Invalid = NonNullable<MapSettingsRequest['invalid']>

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const camel = (key: string): string => key.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase())

const KNOWN_ENVIRONMENT_KEYS = [
  'date',
  'time_of_day',
  'utc_offset',
  'atmosphere',
  'stars',
  'weather',
  'lighting',
  'stay_above_ground',
]

/** Map the model's snake_case environment object onto the page's patch. Type-checks only; ranges are mergeEnvironment's job. */
function readEnvironmentObject(raw: Record<string, unknown>): { patch: EnvironmentPatch } | { error: string } {
  const unknown = Object.keys(raw).find(k => !KNOWN_ENVIRONMENT_KEYS.includes(k))

  if (unknown !== undefined) {
    return { error: `unknown environment field "${unknown}"; known: ${KNOWN_ENVIRONMENT_KEYS.join(', ')}` }
  }

  if (Object.keys(raw).length === 0) return { error: 'environment has no fields to change' }

  const patch: Record<string, unknown> = {}
  const sun: Record<string, unknown> = {}
  if ('date' in raw) sun.date = raw.date
  if ('time_of_day' in raw) sun.timeOfDay = raw.time_of_day
  if ('utc_offset' in raw) sun.utcOffset = raw.utc_offset
  if (Object.keys(sun).length > 0) patch.sun = sun
  if ('atmosphere' in raw) patch.atmosphere = raw.atmosphere
  if ('stars' in raw) patch.stars = raw.stars
  if ('weather' in raw) patch.weather = raw.weather
  if ('lighting' in raw) patch.lightingMode = raw.lighting
  if ('stay_above_ground' in raw) {
    const v = raw.stay_above_ground

    if (typeof v !== 'boolean') return { error: 'stay_above_ground must be true or false' }
    patch.navigationConstraint = v ? 'stayAbove' : 'none'
  }

  return { patch: patch as EnvironmentPatch }
}

/**
 * Read the `set_map_settings` tool input. Pure and never throws. Returns null only when the input is not an
 * object or sets none of engine / view_type / widgets / environment. A field it cannot read is reported under
 * `invalid` (and left out) so the page can refuse it by name.
 */
export function readMapSettings(input: unknown): MapSettingsRequest | null {
  if (!isRecord(input)) return null
  const request: MapSettingsRequest = {}
  const invalid: Invalid = {}
  let touched = false

  if (input.engine !== undefined) {
    touched = true
    if (input.engine === 'openlayers' || input.engine === 'arcgis') request.engine = input.engine
    else invalid.engine = 'engine must be "openlayers" or "arcgis"'
  }

  if (input.view_type !== undefined) {
    touched = true
    if (input.view_type === '2d' || input.view_type === '3d') request.viewType = input.view_type
    else invalid.viewType = 'view_type must be "2d" or "3d"'
  }

  if (input.widgets !== undefined) {
    touched = true
    if (isRecord(input.widgets)) {
      const widgets: Record<string, unknown> = {}
      for (const [key, value] of Object.entries(input.widgets)) widgets[camel(key)] = value
      request.widgets = widgets as MapSettingsRequest['widgets']
    } else invalid.widgets = 'widgets must be an object of widget names to true or false'
  }

  if (input.environment !== undefined) {
    touched = true
    if (input.environment === 'reset') request.environment = 'reset'
    else if (isRecord(input.environment)) {
      const read = readEnvironmentObject(input.environment)

      if ('error' in read) invalid.environment = read.error
      else request.environment = read.patch
    } else invalid.environment = 'environment must be "reset" or an object of environment settings'
  }

  if (!touched) return null
  if (Object.keys(invalid).length > 0) request.invalid = invalid

  return request
}
